/**
 * PD-15071 — how much has actually been paid against an invoice.
 *
 * Moved out of InvoiceBalanceService so the invoice totals recalculation can
 * use the same rules. Those two disagreed: the totals recalculation wrote the
 * whole invoice total into the outstanding balance, so editing a line on a
 * settled invoice made it read as fully unpaid and ask the customer for money
 * already collected. Both now net payments the same way, because there is one
 * definition of "paid".
 *
 * The rules come from the legacy supply-mover-api and are preserved exactly.
 * Transactions are grouped into THREADS by their origin transaction — an
 * authorization, the capture that settles it, a later refund, a void of that
 * refund all belong to one thread — and each thread is netted on its own
 * before the threads are summed. That is what lets a partial refund reduce a
 * payment without erasing it, and a void of a refund restore it.
 *
 * Amounts are the amount APPLIED to this invoice (the link row), not the
 * amount charged to the card, so an invoice paid as part of a larger payment
 * counts only its own share.
 */

import { InvoiceStatus } from '../enums/invoice-status.enum';
import { isPaymentNotCollectedStatus } from './payment-failure-status';

export const INVOICE_PAYMENT_TYPE = {
	AUTHORIZATION: 'Authorization',
	CAPTURE: 'Capture',
	SALE: 'Sale',
	VOID: 'Void',
	REFUND: 'Refund',
} as const;

export const INVOICE_PAYMENT_STATUS_FAILED = 'Failed';

/**
 * PD-16016 — the status a charge carries once it has been voided, either by
 * our own void path or by the ERP import mirroring a payment the ERP
 * cancelled. It is deliberately NOT in `PAYMENT_NOT_COLLECTED_STATUSES`: a
 * `Void` ROW carries this status too, and the thread math needs those rows to
 * keep reversing their parent.
 */
export const INVOICE_PAYMENT_STATUS_VOIDED = 'Voided';

export const INVOICE_FEE_UNIT = {
	PERCENT: '%',
	FLAT_RATE: '$',
} as const;

export interface InvoicePaymentForCalculationModel {
	id: number;
	paymentType: string;
	paymentStatus: string;
	/** Gross amount charged at the gateway, fee included. */
	paymentAmount: number;
	paymentFeeUnit?: string | null;
	paymentFeeAmount?: number | null;
	paymentTransactionParentId?: number | null;
	paymentTransactionOriginId?: number | null;
	/** The share of that payment applied to THIS invoice. */
	invoicePaymentAmount: number;
}

/**
 * Strip the processing fee back out of a gross charge, so a parent
 * authorization can be compared against the fee-exclusive figure applied to
 * the invoice.
 */
function netOfFees(
	paymentAmount: number,
	feeUnit?: string | null,
	feeAmount?: number | null,
): number {
	if (paymentAmount === 0) {
		return 0;
	}

	if (
		feeUnit === INVOICE_FEE_UNIT.PERCENT &&
		feeAmount != null &&
		feeAmount !== 0
	) {
		return paymentAmount / (1 + feeAmount / 100);
	}

	if (
		feeUnit === INVOICE_FEE_UNIT.FLAT_RATE &&
		feeAmount != null &&
		feeAmount !== 0
	) {
		return paymentAmount - feeAmount;
	}

	return paymentAmount;
}

/**
 * PD-16016 — a CHARGE (capture or sale) that has been voided moved no money.
 *
 * Our own void path records a separate `Void` row that reverses the parent, so
 * this case already netted to zero. The ERP import does not: it mirrors a
 * payment the ERP cancelled by flipping the row's status to `Voided` with no
 * `Void` row anywhere, and the charge then kept counting as collected. That is
 * how an invoice the ERP still shows as owing read as paid in full here.
 *
 * Scoped to charges on purpose. A voided REFUND must keep subtracting, because
 * the `Void` row that reversed it adds the same figure straight back.
 */
function isVoidedCharge(
	transaction: InvoicePaymentForCalculationModel | undefined,
): boolean {
	if (transaction == null) {
		return false;
	}

	return (
		transaction.paymentStatus === INVOICE_PAYMENT_STATUS_VOIDED &&
		(transaction.paymentType === INVOICE_PAYMENT_TYPE.CAPTURE ||
			transaction.paymentType === INVOICE_PAYMENT_TYPE.SALE)
	);
}

/**
 * PD-16379 — what ONE transaction did to the amount paid on an invoice,
 * signed: positive when it paid the invoice down, negative when it put money
 * back on it (a refund, or a void of a charge). Zero for rows that moved no
 * money (authorizations, failed or held attempts, voided charges and the
 * voids that reversed them).
 */
export interface InvoicePaymentApplicationModel {
	/** The transaction row id (`InvoicePaymentForCalculationModel.id`). */
	id: number;
	amount: number;
}

/**
 * Net one thread, one transaction at a time: captures and sales add, refunds
 * subtract, and a void reverses whichever it was applied to. Each row's
 * effect is reported on its own, in thread order, so a statement can date
 * every movement; the thread's net is their sum.
 */
function calculateThreadApplications(
	thread: Array<InvoicePaymentForCalculationModel>,
): Array<InvoicePaymentApplicationModel> {
	const applications: Array<InvoicePaymentApplicationModel> = [];

	const byId = new Map<number, InvoicePaymentForCalculationModel>();
	for (const transaction of thread) {
		byId.set(transaction.id, transaction);
	}

	for (const current of thread) {
		// A failed transaction never moved money — and neither did a decline,
		// an error, or a hold-for-review the processor has not approved
		// (PD-15305: the single 'Failed' literal let those net in as paid).
		if (isPaymentNotCollectedStatus(current.paymentStatus)) {
			applications.push({ id: current.id, amount: 0 });
			continue;
		}

		// PD-16016 — a voided charge contributed nothing, so it is skipped and so
		// is the `Void` row that reversed it (handled below); netting them against
		// each other would be the same answer, but only when both rows exist.
		if (isVoidedCharge(current)) {
			applications.push({ id: current.id, amount: 0 });
			continue;
		}

		const parent =
			current.paymentTransactionParentId != null
				? byId.get(current.paymentTransactionParentId)
				: undefined;
		const applied = current.invoicePaymentAmount;
		let amount = 0;

		if (
			current.paymentType === INVOICE_PAYMENT_TYPE.CAPTURE ||
			current.paymentType === INVOICE_PAYMENT_TYPE.SALE
		) {
			amount += applied;

			// A capture settling something that is NOT an authorization is
			// replacing an earlier charge rather than adding to it, so the
			// earlier one comes back off — at the moment of the replacement,
			// which is why it is booked against the replacing capture.
			if (
				parent != null &&
				parent.paymentType !== INVOICE_PAYMENT_TYPE.AUTHORIZATION &&
				!isVoidedCharge(parent)
			) {
				amount -= netOfFees(
					parent.paymentAmount,
					parent.paymentFeeUnit,
					parent.paymentFeeAmount,
				);
			}
		}

		// PD-16016 — when the parent charge was skipped above it never added
		// anything, so reversing it here would take the money off twice.
		if (
			current.paymentType === INVOICE_PAYMENT_TYPE.VOID &&
			parent != null &&
			!isVoidedCharge(parent)
		) {
			// Voiding a refund puts the money back; voiding a capture takes it away.
			if (parent.paymentType === INVOICE_PAYMENT_TYPE.REFUND) {
				amount += applied;
			} else {
				amount -= applied;
			}
		}

		if (current.paymentType === INVOICE_PAYMENT_TYPE.REFUND) {
			amount -= applied;
		}

		applications.push({ id: current.id, amount });
	}

	return applications;
}

/** Group an invoice's transactions into threads by origin transaction. */
function groupIntoThreads(
	transactions: Array<InvoicePaymentForCalculationModel>,
): Array<Array<InvoicePaymentForCalculationModel>> {
	const threads = new Map<number, Array<InvoicePaymentForCalculationModel>>();

	for (const transaction of transactions) {
		const originId = transaction.paymentTransactionOriginId ?? transaction.id;
		const thread = threads.get(originId);

		if (thread == null) {
			threads.set(originId, [transaction]);
		} else {
			thread.push(transaction);
		}
	}

	return Array.from(threads.values());
}

/**
 * PD-16379 — the amount paid on an invoice broken down by the transaction
 * that moved it, one entry per input transaction. Summing the entries gives
 * `calculateInvoiceAmountPaid` exactly (that function is this sum), so a
 * statement that dates each entry by its transaction can roll an invoice's
 * stored balance back to any earlier day without a second definition of
 * "paid".
 *
 * `transactions` are the rows linked to ONE invoice, each carrying the share
 * applied to that invoice — the same input `calculateInvoiceAmountPaid` takes.
 */
export function calculateInvoicePaymentApplications(
	transactions: Array<InvoicePaymentForCalculationModel>,
): Array<InvoicePaymentApplicationModel> {
	return groupIntoThreads(transactions).flatMap((thread) =>
		calculateThreadApplications(thread),
	);
}

/**
 * Total applied to the invoice across every payment thread. Authorizations
 * contribute nothing on their own — a hold is not a payment — which is why an
 * invoice awaiting delivery still reads as owing.
 */
export function calculateInvoiceAmountPaid(
	transactions: Array<InvoicePaymentForCalculationModel>,
): number {
	let total = 0;
	for (const application of calculateInvoicePaymentApplications(transactions)) {
		total += application.amount;
	}

	return total;
}

/**
 * What the invoice still owes: its total less what has been paid, never
 * negative. An overpayment is money on the customer's account, not a negative
 * invoice.
 *
 * PD-15441 — a CREDIT MEMO (negative total) mirrors the rule: its balance is
 * what is still owed TO the customer, tracked as a negative number (the
 * PD-15026 convention) and floored at zero from the other side. Applying part
 * of the memo against invoices records a negative application, which walks
 * the balance from `total` up toward zero: a -$1000 memo with -$400 applied
 * has -$600 of credit left. Without this branch the invoice-side
 * `max(0, ...)` would zero the memo's remaining credit on the first partial
 * application.
 */
export function calculateInvoiceOutstandingBalance(
	invoiceTotal: number,
	transactions: Array<InvoicePaymentForCalculationModel>,
): number {
	const amountPaid = calculateInvoiceAmountPaid(transactions);

	if (invoiceTotal < 0) {
		return Math.min(0, invoiceTotal - amountPaid);
	}

	return Math.max(0, invoiceTotal - amountPaid);
}

/**
 * The payment status shown on an invoice or credit memo.
 *
 * PD-15026 — credit memos need their own vocabulary. A credit memo carries a
 * NEGATIVE outstanding balance (we owe the customer), and the invoice rule
 * `balance > 0 ? 'Outstanding' : 'Paid in Full'` therefore labelled every
 * unrefunded credit "Paid in Full" — the exact opposite of the truth, on the
 * one document where getting it wrong means the customer never gets paid.
 *
 * A credit is OUTSTANDING while we still owe it and APPLIED once the balance
 * has been cleared, whether by refund or by offsetting an invoice.
 *
 * A CANCELLED document reports "Cancelled": its balance is whatever it
 * happened to be when it was cancelled, so both readings the binary offers
 * ("Paid in Full" for a zeroed balance, "Outstanding" for a leftover one)
 * misstate a document nobody owes anything on — and the invoice lists have
 * no separate document-status column, so an empty value left cancelled
 * invoices indistinguishable from live ones (owner feedback on PD-15224).
 */
export function deriveInvoicePaymentStatus(
	outstandingBalance: number | null,
	isCreditMemo: boolean,
	invoiceStatus?: string | null,
): string | null {
	if (invoiceStatus === InvoiceStatus.CANCELLED) {
		return 'Cancelled';
	}

	if (outstandingBalance == null) {
		return null;
	}

	// PD-16747 — a CLOSED document is finished, whatever balance it still
	// carries. An ERP-mastered invoice can be closed by its ERP while Workd's
	// stored figure lags (HF's CAP-68895 sat at Closed with $84.45 left on it),
	// and "Outstanding" told staff to chase money nobody owes. The storefront
	// already says "Closed" for the same case (PD-16381). A closed document
	// with nothing left on it keeps its ordinary paid reading.
	const isClosedWithBalance =
		invoiceStatus === InvoiceStatus.CLOSED &&
		(isCreditMemo
			? creditMemoRemainingCredit(outstandingBalance) > 0
			: outstandingBalance > 0);

	if (isClosedWithBalance) {
		return 'Closed';
	}

	if (isCreditMemo) {
		// PD-15441 — read the MAGNITUDE, not the sign. The two producers of a
		// credit memo's remaining balance disagree: the ERP sync stores it
		// POSITIVE (HF Foods) while SM-issued memos store it NEGATIVE
		// (PD-15026). Testing `< 0` therefore called every ERP-synced RMA
		// "Credit Applied" while the customer was still owed the money — the
		// same mistake the invoice rule made before PD-15026, one convention
		// further along. Any non-zero balance means credit is still owed.
		return creditMemoRemainingCredit(outstandingBalance) > 0
			? 'Credit Outstanding'
			: 'Credit Applied';
	}

	return outstandingBalance > 0 ? 'Outstanding' : 'Paid in Full';
}

/**
 * PD-15441 — the credit still available on a credit memo, as a POSITIVE
 * magnitude, whichever convention stored it.
 *
 * The ERP sync writes a memo's remaining balance positive (HF Foods'
 * Workday feed) while an SM-issued memo writes it negative (PD-15026). Both
 * mean the same thing — "this much credit is still owed to the customer" —
 * so every display and comparison takes the magnitude rather than picking a
 * side. Accepts the raw string the DTOs carry as well as a parsed number.
 *
 * Returns 0 for null/unparseable input: an unknown balance is not credit we
 * can offer to spend.
 */
export function creditMemoRemainingCredit(
	outstandingBalance: number | string | null | undefined,
): number {
	if (outstandingBalance == null) {
		return 0;
	}
	const parsed =
		typeof outstandingBalance === 'string'
			? parseFloat(outstandingBalance)
			: outstandingBalance;
	return Number.isFinite(parsed) ? Math.abs(parsed) : 0;
}

/**
 * PD-16747 — the balance an invoice LIST shows in its Amount Owed column.
 *
 * A credit memo's balance is credit the customer still has, not money they
 * owe. HF's Workday feed stores it POSITIVE, so the column showed an unspent
 * $28.50 credit as $28.50 owed. A memo's remaining credit is therefore shown
 * as a NEGATIVE figure whichever convention stored it, beside a "Credit
 * Outstanding" badge. A zero balance shows as nothing (the lists' existing
 * convention), and so does an unknown one.
 */
export function deriveListOutstandingBalance(
	outstandingBalance: number | string | null | undefined,
	isCreditMemo: boolean,
): number | null {
	if (outstandingBalance == null) {
		return null;
	}

	const parsed =
		typeof outstandingBalance === 'string'
			? parseFloat(outstandingBalance)
			: outstandingBalance;

	if (!Number.isFinite(parsed) || parsed === 0) {
		return null;
	}

	return isCreditMemo ? -creditMemoRemainingCredit(parsed) : parsed;
}

/**
 * How much of a credit memo has been used, as a display state.
 *
 * The customer-facing counterpart to `deriveInvoicePaymentStatus`'s
 * Outstanding / Paid in Full split. A credit memo's good state is the
 * INVERSE of an invoice's: credit still available is what the customer
 * wants, so `available` is the reassuring state and `used` the spent one.
 *
 *   available — none of it applied yet
 *   partial   — some applied, some credit left
 *   used      — fully applied, nothing left to spend
 *
 * Both figures are read as magnitudes (see `creditMemoRemainingCredit`).
 * A memo with no recorded balance falls back to its total, i.e. fully
 * available — matching what the payable-invoice list does with a NULL
 * balance.
 */
export function deriveCreditMemoApplicationStatus(
	outstandingBalance: number | string | null | undefined,
	total: number | string | null | undefined,
): 'available' | 'partial' | 'used' {
	const issued = creditMemoRemainingCredit(total);
	const remaining =
		outstandingBalance == null
			? issued
			: creditMemoRemainingCredit(outstandingBalance);

	// Cent tolerance on both edges: the balance is stored to two decimals but
	// arrives through float parsing, and a memo one cent from depleted should
	// not read as still partly available.
	if (remaining <= 0.005) {
		return 'used';
	}
	if (issued > 0 && remaining < issued - 0.005) {
		return 'partial';
	}
	return 'available';
}
